/* eslint-disable no-useless-escape */
import React, { useEffect, useState } from "react";
import { ConfirmOrderByParentModel, ConfirmOrderStatus } from "../../../Store/ConfirmOrder/types";
import SVG from "react-inlinesvg";
import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../../../Components/Forms/Input";
import Button from "../../../Components/Button/Button";
import Select, { components } from "react-select";
import SelectBoxCustomOptionLabel from "../../../Components/SelectBox/SelectBoxCustomOptionLabel";
import { SelectOptionObjectType } from "../../../Services/Models/Misc/HelperModels";
import { USStateRegExp, cityNameRegExp, nameRegExp, zipRegExp } from "../../../Methods/Helpers/RegularExpressions";
import { useParams } from "react-router-dom";
import { fetchOrderDetails, updateOrderDetails, verifyParentEmail, verifyParentEmailCode } from "../../../Store/ConfirmOrder/ConfirmOrderAPI";
import ConfirmParentEmail from "./Components/ConfirmParentEmail";
import { toastError, toastWarning } from "../../../Methods/Helpers/ToastHelper";
import ConfirmOrderStatusHandler from "./Components/ConfirmOrderStatusHandler";
import SkeletonLoading from "../../../Components/Skeleton/SkeletonLoading";
import US_STATE_LIST from '../../../Services/Constants/USStatesList.json';
import { SelectBoxFloating } from "../../../Components/SelectBox/SelectBoxFloating";
import { SlackPostChat } from "../../../Methods/Slack/SlackPostChat";

const { ValueContainer, Placeholder } = components;

const patientInitialState: ConfirmOrderByParentModel = {
    firstname: "",
    surname: "",
    address: "",
    address2: "",
    shipping_country: "USA",
    city: "",
    state: "",
    zip: "",
    sku: undefined, //set same value's isChecked true in the options array
    childName: "",
    leadEmail: "",
    current_status: "",
    provider_tag: "NA"
};

const CustomValueContainer = ({ children, ...props }: any) => {
    return (
        <ValueContainer {...props}>
            <Placeholder {...props} isFocused={props.isFocused}>
                {props.selectProps.placeholder}
            </Placeholder>
            {React.Children.map(children, child =>
                child && child.type !== Placeholder ? child : null
            )}
        </ValueContainer>
    );
};

const ConfirmOrderByPatient = () => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
    const [status, setStatus] = useState<ConfirmOrderStatus>(undefined);
    const [statusDetails, setStatusDetails] = useState<any>(undefined);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [selectedCover, setSelectedCover] = useState<any>({ value: "", label: "", image: "", isChecked: false });
    const [orderDetails, setOrderDetails] = useState<any>();
    const [contactEmail, setContactEmail] = useState<string>("");

    useEffect(() => {
        if(!isEmailVerified){
            fetchOrderData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formik = useFormik({
        validateOnMount: true,
        initialValues: patientInitialState,
        validationSchema: Yup.object({
            firstname: Yup.string().required("First name is required").matches(nameRegExp, "Invalid Character").min(1, "Minimum 1 characters are required").max(40, "Max 15 characters are allowed").nullable(),
            surname: Yup.string().required("Surname is required").matches(nameRegExp, "Invalid Character").min(1, "Minimum 1 characters are required").max(40, "Max 15 characters are allowed").nullable(),
            address: Yup.string().required("Address is required").min(10, "Minimum 10 characters required").max(80, "Max 80 character are allowed").matches(/^(?![ ]+$)[A-Za-z0-9'\.\-\s\,]+$/, "Invalid Character").nullable(),
            address2: Yup.string().nullable().min(2, "Minimum 2 characters required").max(80, "Max 80 character are allowed").matches(/^(?![ ]+$)[A-Za-z0-9'\.\-\s\,]+$/, "Invalid Character"),
            state: Yup.string().required("State is required").matches(USStateRegExp, "Invalid Information").nullable(),
            zip: Yup.string().matches(zipRegExp, "Zip is not valid").required("Zip is required"),
            sku: Yup.string().required("Please select a top cover set"),
            city: Yup.string().required("City is required").matches(cityNameRegExp, "Invalid Characters").max(30, "Max 30 characters allowed").nullable(),
        }),
        onSubmit: (values) => {
            setIsLoading(true);
            handleOrderConfirm(values);
        },
    });

    const fetchOrderData = async () => {
        try {
            if (id) {
                setStatus(undefined);
                const response: any = await fetchOrderDetails(id);
                if (response.success){
                    let data = response.data;
                    if (data.order_status.current_status === "Not owned") {
                        let tempData: ConfirmOrderByParentModel = {
                            firstname: data?.lead_mongo_data.ecFirstName ?? undefined,
                            surname: data?.lead_mongo_data.ecLastName ?? undefined,
                            leadEmail: data?.lead.lead_email ?? undefined,
                            address: data?.patient_data.address ?? undefined,
                            address2: data?.patient_data.address2 ?? undefined,
                            state: data?.patient_data.state ?? undefined,
                            zip: data?.patient_data.zipcode ?? undefined,
                            childName: data?.patient_data.firstname ?? undefined,
                            sku: undefined,
                            current_status: data?.order_status.current_status ?? undefined,
                            shipping_country: "USA",
                            provider_tag: data?.order_info.provider_tag ?? "NA",
                            city: data?.patient_data.city ?? undefined
                        }
                        formik.setValues({...tempData}, true);
                        let keys = Object.keys(tempData);
                        const validKeys: (keyof ConfirmOrderByParentModel)[] = keys as (keyof ConfirmOrderByParentModel)[];

                        validKeys.forEach((el: keyof ConfirmOrderByParentModel) => {
                            if ((el !== "sku" && el !== "provider_tag" && el !== "current_status" && el !== "shipping_country" && el !== "address2") && tempData[el] === undefined) {
                                handleEditClick()                                
                            }
                        });
                        setContactEmail(data.lead.lead_email);
                        setOrderDetails(tempData);
                        handleSendVerificationCode(data.lead.lead_email);
                        setIsLoading(false);
                    } else {
                        setStatus("INVALID_ORDER");
                        setStatusDetails(data.order_status.current_status);
                    }
                } else {
                    if(response.code === 404) {
                        setStatus("INVALID_ORDER_ID");
                    } else {
                        setStatus("RESPONSE_ERROR");
                    }
                }
            } else {
                setStatus("ORDER_ID_MISSING");
                setIsLoading(false);
            }
        } catch (error: any) {
            setIsLoading(false);
            setStatus("RESPONSE_ERROR");
            console.log("error", error.toString());
        }
    }

    const handleSendVerificationCode = async (email: string) => {
        try {
            setIsLoading(true);
            let response: any = await verifyParentEmail(email);
            if (response.success && response.code === "CODE_SENT"){
                setStatus(undefined);
                setIsLoading(false)
            } else {
                setIsLoading(false);
                setStatus("EMAIL_ERROR");
                setStatusDetails(response.message);
            }
        } catch (error: any) {
            setIsLoading(false);
            console.log("error", error.toString());
        }
    }

    const handleVerifyCode = async (code: string) => {
        try {
            setIsLoading(true);
            // setIsEmailVerified(true);
            await verifyParentEmailCode(orderDetails.leadEmail, code).then((resp: any) => {
                if (resp.data.status && resp.data.data.verification_status === "CODE_VERIFIED") {
                    setStatus(undefined)
                    setIsEmailVerified(true);
                } else if (!resp.data.status && resp.data.data.verification_status === "USER_NOT_AUTHORIZED") {
                    setIsEmailVerified(false);
                    setIsLoading(false);
                    toastError("Invalid verification code, please enter a valid code");
                } else if (!resp.data.status && resp.data.data.verification_status === "CODE_EXPIRED") {
                    setIsEmailVerified(false);
                    setIsLoading(false);
                    if (contactEmail){
                        toastWarning("Verification code is expired, new code has been sent on "+contactEmail);
                        handleSendVerificationCode(contactEmail);
                    } else {
                        toastError("Invalid verification code");
                    }
                }
                setIsLoading(false);
            }).catch((error: any) => {
                setIsLoading(false);
                toastError(error.message || error.toString());
            });
        } catch (error: any) {
            setIsLoading(false);
            console.log(error.code + ": " + error.message || error.toString());
        }
    }

    const handleOrderConfirm = async (values: any) => {
        try {
            if(id){
                let letFormData = {
                    firstname: values.firstname,
                    surname: values.surname,
                    shipping_address: values.address,
                    shipping_address2: values.address2,
                    shipping_country: values.shipping_country,
                    zip: values.zip,
                    state: values.state,
                    sku: values.sku,
                    city: values.city,
                }
                await updateOrderDetails(id, letFormData).then((resp: any) => {
                    setIsLoading(false);
                    if(resp.success) {
                        setStatus("ORDER_CONFIRMED");
                        setStatusDetails(orderDetails.childName);
                        SlackPostChat({ provider: orderDetails.provider_tag, quantity: 1, origin: "Provider Portal" });
                    } else {
                        toastError("Error in updating order, please try again")
                        setIsLoading(false);
                    }
                }).catch((error: any) => {
                    setIsLoading(false);
                    toastError(error.message || error.toString());
                });
                setIsLoading(false);
            } else {
                setStatus("ORDER_ID_MISSING");
                setIsLoading(false);
            }
        } catch (error: any) {
            setStatus("RESPONSE_ERROR");
            setIsLoading(false);
        }
    }

    const handleEditClick = () => {
        setIsEdit(true);
    }

    const onBandCoverSelectChange = (e: SelectOptionObjectType) => {
        setSelectedCover(e)
        formik.setValues({ ...formik.values, sku: e.value });
    }

    const onStateSelectChange = (value: any) => {
        formik.setValues({ ...formik.values, state: value.value });
        // formik.setTouched({ ...formik.touched, state: true }, true);
    }

    const onStateSelectChangeBlur = (value: any) => {
        formik.setTouched({ ...formik.touched, state: true }, true);
    }

    const formatOptionLabel = ({ label, value, image, isChecked }: any, { context }: any) => {
        // Check if the context is the dropdown menu or the value container
        const isMenu = context === 'menu';
        if (isMenu) {
            return (
                <>
                    <div className="custom-radio cover-watches-radio">
                        <input type="radio" name="cover" id={label.toLowerCase().split(' ').join('_')} checked={selectedCover.value === value} />
                        <label htmlFor={label.toLowerCase().split(' ').join('_')} className="label">
                            <span className="cover-watches-items">
                                <span className="cover-images">
                                    <img src={image} alt={label.toLowerCase().split(' ').join('-')} width={130} height={120} />
                                </span>
                                <span className="name w-100">{label}</span>
                            </span>
                        </label>
                    </div>
                </>
            );
        }

        return <span>{label}</span>;
    };

    return (
        <>
            {!status ? (
                <>
                    {!isEmailVerified ? (
                        <ConfirmParentEmail 
                        email={orderDetails?.leadEmail || ""} 
                        isEmailVerified={isEmailVerified}
                        setIsEmailVerified={setIsEmailVerified}
                        isLoading={isLoading}
                        steIsLoading={setIsLoading}
                        sendVerificationCode={handleSendVerificationCode}
                        handleVerifyCode={handleVerifyCode}
                        />
                    ) : (
                        <>
                            <div className="main-wrapper confirm-order-screens">
                                <div className="cards-wrap card-success">
                                    <div className="card-body confirm-order-model">
                                        <div className="header">
                                            <div className="logo mb-4">
                                                {" "}
                                                <SVG
                                                    className="header-logo"
                                                    src="Assets/Images/Logos/kiddo.svg"
                                                    width="108"
                                                    height="32"
                                                />{" "}
                                            </div>
                                            <p className="title-medium text-center">
                                                Please confirm shipping details and <br /> band preferences for your child, {orderDetails?.childName}.
                                            </p>
                                        </div>
                                        <div className="card-inner confirm-order">
                                                <h5 className={`section-title ${!isEdit && "px-3"}`}>Shipping Details</h5>
                                                {!isEdit && (
                                                    <>
                                                        <div className="address-details-wrap d-flex justify-content-between align-items-end px-3">
                                                            {isLoading ? (
                                                                <>
                                                                    <address>
                                                                        <SkeletonLoading width={200} height={24} />
                                                                        <br />
                                                                        <SkeletonLoading width={180} height={24} />
                                                                        <br />
                                                                        <SkeletonLoading width={180} height={24} />
                                                                        <br />
                                                                        <SkeletonLoading width={160} height={24} />
                                                                    </address>
                                                                    <div className="btn-wrap">
                                                                        <SkeletonLoading width={95} height={56} />
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <address>
                                                                        {formik.values.firstname} {formik.values.surname} <br />
                                                                        {formik.values.address ? formik.values.address : ""}{formik.values.address2 ? (<>, <br /> {formik.values.address2},<br /></>) : ""}
                                                                        {formik.values.city} {formik.values.state}, {formik.values.shipping_country} - {formik.values.zip}
                                                                    </address>
                                                                    <div className="btn-wrap">
                                                                        <button onClick={handleEditClick} className="btn mybtn btn-success success-reverse">Edit</button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                <div className="address-details-form">
                                                    <form className="" noValidate onSubmit={(e) => {
                                                        e.preventDefault();
                                                        formik.handleSubmit();
                                                        return false;
                                                    }}>
                                                        {isEdit && (
                                                            <>
                                                                {isLoading ? (
                                                                    <>
                                                                        {[1,2,3,4,5,6].map(() => {
                                                                            return (
                                                                                <>
                                                                                    <div className="mb-2">
                                                                                        <SkeletonLoading width={420} height={41} />
                                                                                    </div>
                                                                                </>
                                                                            )
                                                                        })}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="mb-5">
                                                                            <Input
                                                                                name="firstname"
                                                                                label="First Name *"
                                                                                inputValue={formik.values.firstname}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.firstname ? true : false}
                                                                                errorMessage={formik.errors.firstname && formik.errors.firstname} />
                                                                            <Input
                                                                                name="surname"
                                                                                label="Surname *"
                                                                                inputValue={formik.values.surname}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.surname ? true : false}
                                                                                errorMessage={formik.errors.surname && formik.errors.surname} />
                                                                            <Input
                                                                                name="address"
                                                                                label="Address *"
                                                                                inputValue={formik.values.address}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.address ? true : false}
                                                                                errorMessage={formik.errors.address && formik.errors.address} />
                                                                            <Input
                                                                                name="address2"
                                                                                label="Address Line 2"
                                                                                inputValue={formik.values.address2}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.address2 ? true : false}
                                                                                errorMessage={formik.errors.address2 && formik.errors.address2} />
                                                                            <Input
                                                                                name="city"
                                                                                label="City *"
                                                                                inputValue={formik.values.city}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.city ? true : false}
                                                                                errorMessage={formik.errors.city && formik.errors.city} />
                                                                            {/* <Input
                                                                                name="state"
                                                                                label="State *"
                                                                                inputValue={formik.values.state}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.state ? true : false}
                                                                                errorMessage={formik.errors.state && formik.errors.state} /> */}
                                                                                <div className="pt-2">
                                                                                    <Select
                                                                                        className="basic-single"
                                                                                        classNamePrefix="select"
                                                                                        name="state"
                                                                                        options={US_STATE_LIST}
                                                                                        components={{
                                                                                            ValueContainer: CustomValueContainer
                                                                                        }}
                                                                                        value={US_STATE_LIST.filter((el: any) => el.value === formik.values.state)}
                                                                                        placeholder="State"
                                                                                        styles={SelectBoxFloating(false)}
                                                                                        onChange={(e: any) => onStateSelectChange(e)}
                                                                                        onBlur={(e: any) => onStateSelectChangeBlur(e)}
                                                                                    />
                                                                                    {formik.errors.state && formik.touched.state ? <label className="text-error">{formik.errors.state}</label> : <label className="text-error"></label>}
                                                                                </div>
                                                                            <Input
                                                                                name="zip"
                                                                                label="Zip *"
                                                                                inputValue={formik.values.zip}
                                                                                onBlur={formik.handleBlur}
                                                                                onChange={formik.handleChange}
                                                                                type="text"
                                                                                isError={formik.errors.zip ? true : false}
                                                                                errorMessage={formik.errors.zip && formik.errors.zip} />
                                                                        </div>
                                                                    </>
                                                                )}
                                                                
                                                            </>
                                                        )}
                                                        <h5 className="section-title">Choose band preference</h5>
                                                        {isLoading ? (
                                                            <>
                                                                <div className="cover-dropdown">
                                                                    <SkeletonLoading width={420} height={46} />
                                                                </div>
                                                                <div className="btn-wrap">
                                                                    <SkeletonLoading width={420} height={46} />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="cover-dropdown">
                                                                    <Select
                                                                        name="sku"
                                                                        id="sku"
                                                                        placeholder="Choose a top cover set"
                                                                        styles={SelectBoxCustomOptionLabel}
                                                                        formatOptionLabel={formatOptionLabel}
                                                                        menuShouldScrollIntoView
                                                                        options={[
                                                                            { value: "Robot", label: "Robot top covers", image: "Assets/Images/Product/robot-cover.png", isChecked: selectedCover.value === "Robot" },
                                                                            // { value: "Dolphin", label: "Dolphin top covers", image: "Assets/Images/Product/dolphin-cover.png", isChecked: selectedCover.value === "Dolphin" },
                                                                            { value: "Dinosaur", label: "Dino top covers", image: "Assets/Images/Product/dino-cover.png", isChecked: selectedCover.value === "Dinosaur" }
                                                                        ]}
                                                                        onChange={(e: any) => onBandCoverSelectChange(e)}
                                                                        // value={selectedCover}
                                                                        defaultMenuIsOpen
                                                                    />

                                                                    {formik.errors.sku && <label className="text-error mt-2 mx-2">{formik.errors.sku}</label>}
                                                                </div>
                                                                <Button
                                                                    btnType="submit"
                                                                    customClass={`${!formik.isValid ? "btn-custom-disabled" : "btn-success"}`}
                                                                    primary={true}
                                                                    text={true}
                                                                    label="Save and Order"
                                                                    disabled={!formik.isValid} />
                                                                {!formik.isValid && <label className="text-error mt-2 mx-2">Please check all shipping details</label>}
                                                            </>
                                                        )}
                                                    </form>
                                                </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            ) : (
                <>
                        <ConfirmOrderStatusHandler statusDetail={statusDetails} statusType={status} resendVerificationCode={() => handleSendVerificationCode(contactEmail)} />
                </>
            )}
        </>
    );
};

export default ConfirmOrderByPatient;
